import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getImageBasePath } from '../../utils/formatters';
import MedalIcon from './MedalIcon';
import ImageLightbox from '../common/ImageLightbox';
import './Achievements.css';

/**
 * Maps achievement category keys to translation key-paths (resolved via t() in
 * the component). Reuses the medals.* category labels shared with MedalsPage.
 * @type {Record<string, string>}
 */
const CATEGORY_KEYS = {
  milestones: 'medals.catMilestones',
  combat: 'medals.catCombat',
  skill: 'medals.catSkill',
  utility: 'medals.catUtility',
};

/**
 * Displays a player's achievements with collected emblems and progress bars.
 * @param {Object} props
 * @param {string} props.steamid64 - Steam 64-bit ID of the player.
 */
const Achievements = ({ steamid64 }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [lightboxAch, setLightboxAch] = useState(null);
  const closeLightbox = useCallback(() => setLightboxAch(null), []);

  useEffect(() => {
    fetch(`${API_URL}/api/achievements/${steamid64}`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
  }, [steamid64]);

  if (!data) return null;

  const unlocked = data.achievements.filter(a => a.unlocked);
  const categories = ['all', ...Object.keys(CATEGORY_KEYS)];
  const filtered = filter === 'all'
    ? data.achievements
    : data.achievements.filter(a => a.category === filter);

  return (
    <div className="achievements-section">
      {/* Collected emblems */}
      {unlocked.length > 0 && (
        <div className="ach-emblems">
          <div className="ach-emblems-header">
            <h3>{t('nav.medals')}</h3>
            <span className="achievements-count">{data.unlocked}/{data.total}</span>
          </div>
          <div className="ach-emblems-grid">
            {unlocked.map(ach => (
              <div
                key={ach.id}
                className="ach-emblem clickable"
                title={`${ach.name} — ${ach.description}`}
                onClick={() => setLightboxAch(ach)}
              >
                <MedalIcon id={ach.id} emoji={ach.icon} size={48} rarity="common" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full progress list */}
      <div className="ach-progress-section">
        <div className="ach-progress-header" onClick={() => setShowAll(!showAll)}>
          <span className="ach-progress-toggle">{showAll ? '▾' : '▸'} {t('medals.allAndProgress')}</span>
        </div>

        {showAll && (
          <>
            <div className="achievements-filters">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`ach-filter-btn ${filter === cat ? 'active' : ''}`}
                  onClick={() => setFilter(cat)}
                >
                  {cat === 'all' ? t('matches.all') : t(CATEGORY_KEYS[cat])}
                </button>
              ))}
            </div>

            <div className="achievements-grid">
              {filtered.map(ach => (
                <div
                  key={ach.id}
                  className={`achievement-card ${ach.unlocked ? 'unlocked clickable' : 'locked'}`}
                  onClick={ach.unlocked ? () => setLightboxAch(ach) : undefined}
                >
                  <div className="ach-icon">
                    <MedalIcon id={ach.id} emoji={ach.icon} size={50} rarity={ach.unlocked ? 'common' : 'locked'} />
                  </div>
                  <div className="ach-info">
                    <div className="ach-name">{ach.name}</div>
                    <div className="ach-desc">{ach.description}</div>
                    <div className="ach-progress-bar">
                      <div
                        className="ach-progress-fill"
                        style={{ width: `${getProgressPct(ach.progress)}%` }}
                      />
                    </div>
                    <div className="ach-progress-text">{ach.progress}</div>
                  </div>
                  {ach.unlocked && <div className="ach-check">✓</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {lightboxAch && (
        <ImageLightbox
          src={`${getImageBasePath()}/images/achievements/${lightboxAch.id}.png`}
          title={lightboxAch.name}
          subtitle={`${lightboxAch.description} — ${lightboxAch.progress}`}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
};

/**
 * Parses a progress string and returns the completion percentage.
 *
 * Handles every format the engine produces: "5/10", "85/85", "1.05/1.00" and
 * the percentage forms "45%/60%" and "22.5%/35%". The old regex required the
 * separator to be a single `/` or `%`, so both percentage formats fell through
 * to 0 and their progress bars always rendered empty.
 *
 * @param {string} progress - Progress string in "current/target" format.
 * @returns {number} Percentage between 0 and 100.
 */
function getProgressPct(progress) {
  if (!progress) return 0;
  const match = progress.match(/^([\d.]+)%?\s*\/\s*([\d.]+)%?$/);
  if (!match) return 0;
  const current = parseFloat(match[1]);
  const target = parseFloat(match[2]);
  if (!Number.isFinite(current) || !Number.isFinite(target)) return 0;
  if (target === 0) return 100;
  return Math.min(100, Math.max(0, (current / target) * 100));
}

export default Achievements;
